'use client';

import type { ReactNode, Ref } from 'react';
import { useId } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * InputField — Figma komponent „Input Field".
 *
 * Rozmery odmerané z Figmy — mobil z hero karty `60042:5246`, desktop zo sekcie
 * Kontakt `47:2043`:
 *   popisok  Label/Field 12 → 14, riadkovanie 20, Tight-05
 *   medzera  popisok → pole: 4
 *   pole     výška 48 → 52 · odsadenie 12 → 20 · rádius 8 · rám #DDE3EC
 *            text Body/M 14 → 16 · placeholder #55617A
 *   textarea pole 96 → 104 vysoké, text zarovnaný hore
 *   checkbox 24×24, rádius 2, rám rgba(0,0,0,0.1)
 *
 * Polia sú riadené zvonku (react-hook-form): berú `value`, `onChange`, `onBlur`,
 * `name` a `ref` presne tak, ako ich dáva `field` z `FormField`. Chybu vykreslia
 * samy cez prop `error` — vlastný `FormMessage` z jadra by so sebou priniesol aj
 * svoje odsadenia a rozbil by rozostupy z Figmy.
 *
 * Stav Error je teda hotový; Hover/Focus/Filled z Figmy sú zatiaľ len cez
 * `focus:border-primary`.
 *
 * Nepoužívam zdieľaný `ui/input.tsx` z jadra: má vlastnú výšku (h-12 sm:h-14),
 * `font-light` a svetlosivý placeholder, čo by som musel na každom poli
 * prebíjať. KAPYCAR pole je iný tvar, nie odchýlka od toho existujúceho.
 *
 * Farby: jediná premenná je `primary`, zvyšok natvrdo hexom.
 */

const CONTROL = cn(
  'w-full rounded-8 border bg-[#FFFFFF] px-3 lg:px-5',
  'text-[14px] leading-[1.5] tracking-[-0.031em] text-[#212121] outline-none',
  'placeholder:text-[#55617A] lg:text-[15px] xl:text-[16px]',
  'transition-colors',
);

const CONTROL_HEIGHT = 'h-12 lg:h-[52px]';

/** Rám poľa — pri chybe červený (Figma Feedback/Error #C42B1C). */
const border = (error?: string) =>
  error ? 'border-[#C42B1C] focus:border-[#C42B1C]' : 'border-[#DDE3EC] focus:border-primary';

type FieldControlProps = {
  id?: string;
  name: string;
  value?: string;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => void;
  onBlur?: () => void;
  disabled?: boolean;
  error?: string;
};

export interface InputFieldProps extends FieldControlProps {
  label: string;
  type?: 'text' | 'tel' | 'email';
  placeholder?: string;
  required?: boolean;
  className?: string;
  ref?: Ref<HTMLInputElement>;
}

export function InputField({
  label,
  name,
  id,
  type = 'text',
  placeholder,
  required,
  className,
  error,
  ref,
  ...field
}: InputFieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  return (
    <FieldShell
      label={label}
      htmlFor={fieldId}
      required={required}
      error={error}
      className={className}
    >
      <input
        {...field}
        ref={ref}
        id={fieldId}
        name={name}
        type={type}
        placeholder={placeholder}
        aria-invalid={!!error}
        aria-required={required || undefined}
        aria-describedby={error ? `${fieldId}-error` : undefined}
        className={cn(CONTROL, CONTROL_HEIGHT, border(error))}
      />
    </FieldShell>
  );
}

export interface TextareaFieldProps extends FieldControlProps {
  label: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
  ref?: Ref<HTMLTextAreaElement>;
}

export function TextareaField({
  label,
  name,
  id,
  placeholder,
  required,
  className,
  error,
  ref,
  ...field
}: TextareaFieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  return (
    <FieldShell
      label={label}
      htmlFor={fieldId}
      required={required}
      error={error}
      className={className}
    >
      <textarea
        {...field}
        ref={ref}
        id={fieldId}
        name={name}
        placeholder={placeholder}
        aria-invalid={!!error}
        aria-required={required || undefined}
        aria-describedby={error ? `${fieldId}-error` : undefined}
        className={cn(CONTROL, border(error), 'h-24 resize-none py-4 lg:h-[104px]')}
      />
    </FieldShell>
  );
}

export interface SelectFieldProps extends FieldControlProps {
  label: string;
  placeholder: string;
  options: { value: string; label: string }[];
  required?: boolean;
  className?: string;
  ref?: Ref<HTMLSelectElement>;
}

export function SelectField({
  label,
  name,
  id,
  placeholder,
  options,
  required,
  className,
  error,
  ref,
  value,
  ...field
}: SelectFieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  return (
    <FieldShell
      label={label}
      htmlFor={fieldId}
      required={required}
      error={error}
      className={className}
    >
      <select
        {...field}
        ref={ref}
        id={fieldId}
        name={name}
        value={value ?? ''}
        aria-invalid={!!error}
        aria-required={required || undefined}
        aria-describedby={error ? `${fieldId}-error` : undefined}
        className={cn(
          CONTROL,
          CONTROL_HEIGHT,
          border(error),
          'cursor-pointer appearance-none pr-10',
        )}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {/* Šípka je vlastná — natívnu appearance vypína `appearance-none`.
          Sedí na hornom okraji poľa, aby ju prípadná chybová hláška neposunula. */}
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute right-4 size-2.5 rotate-45',
          'border-b-2 border-r-2 border-[#212121]',
          'top-[42px] lg:right-6 lg:top-[46px]',
        )}
      />
    </FieldShell>
  );
}

export interface CheckboxFieldProps {
  id?: string;
  name: string;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  onBlur?: () => void;
  error?: string;
  /** Text vedľa políčka. Môže obsahovať odkaz, preto ide ako `children`. */
  children: ReactNode;
  className?: string;
  ref?: Ref<HTMLInputElement>;
}

/** Figma „Checkbox set" (`47:2039`) — políčko 24×24 a text Body/M vedľa neho. */
export function CheckboxField({
  name,
  id,
  checked = false,
  onCheckedChange,
  onBlur,
  error,
  children,
  className,
  ref,
}: CheckboxFieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  return (
    <div className={cn('ea-checkbox-field', 'flex flex-col gap-1', className)}>
      <div className="flex items-center gap-2">
        <input
          ref={ref}
          id={fieldId}
          name={name}
          type="checkbox"
          checked={checked}
          onChange={event => onCheckedChange?.(event.target.checked)}
          onBlur={onBlur}
          aria-invalid={!!error}
          aria-describedby={error ? `${fieldId}-error` : undefined}
          className={cn(
            'size-6 shrink-0 cursor-pointer appearance-none rounded-xs',
            'border bg-[#FFFFFF] transition-colors',
            'checked:border-primary checked:bg-primary',
            error ? 'border-[#C42B1C]' : 'border-[rgba(0,0,0,0.1)]',
          )}
        />
        <label
          htmlFor={fieldId}
          className="cursor-pointer text-[14px] leading-[1.5] tracking-[-0.031em] text-[#212121] lg:text-[15px] xl:text-[16px]"
        >
          {children}
        </label>
      </div>
      <FieldError error={error} id={`${fieldId}-error`} />
    </div>
  );
}

function FieldShell({
  label,
  htmlFor,
  required,
  error,
  className,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  error?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('ea-input-field', 'relative flex w-full flex-col gap-1', className)}>
      <label htmlFor={htmlFor} className="label-field text-[#212121]">
        {label}
        {required && ' *'}
      </label>
      {children}
      <FieldError error={error} id={`${htmlFor}-error`} />
    </div>
  );
}

/** Figma Feedback/Error #C42B1C. */
function FieldError({ error, id }: { error?: string; id: string }) {
  if (!error) return null;
  return (
    <span id={id} className="text-[12px] leading-[1.43] tracking-[-0.036em] text-[#C42B1C]">
      {error}
    </span>
  );
}
